import React, { useEffect } from "react";
import {
    Box,
    Typography,
    Button,
    LinearProgress,
    Table,
    AvatarGroup,
    Avatar,
    Checkbox,
    Stack,
    Divider,
} from "@mui/joy";
import { useNavigate } from "react-router-dom";
import { Plus, MoreHorizontal, Calendar, AlertTriangle, CheckCircle2 } from "lucide-react";
import { IconButton } from "../../../shared";
import { useDashboardData } from "../hooks";

type ProjectState = "Overdue" | "Delayed" | "On Track" | "To Do";

const STATUS_UI_CONFIG: Record<
    string,
    { label: string; color: "warning" | "danger" | "success" | "neutral"; icon: React.ReactNode; priority: number }
> = {
    Overdue: { label: "Overdue", color: "warning", icon: <AlertTriangle size={16} />, priority: 1 },
    Delayed: { label: "Delayed", color: "danger", icon: <AlertTriangle size={16} />, priority: 2 },
    "In Progress": { label: "In Progress", color: "success", icon: <CheckCircle2 size={16} />, priority: 3 },
    "On Track": { label: "On Track", color: "success", icon: <CheckCircle2 size={16} />, priority: 4 },
    "To Do": { label: "To Do", color: "neutral", icon: <AlertTriangle size={16} />, priority: 5 },
    Completed: { label: "Completed", color: "success", icon: <CheckCircle2 size={16} />, priority: 6 },
};

const getAvatarUrl = (id: number) => `https://prium.github.io/aurora/images/avatar/${id}.webp`;

export default function ProjectRoadmap() {
    const { projects } = useDashboardData();
    const navigate = useNavigate();

    const calculateProjectState = (project: any) => {
        const now = new Date();
        const estimatedEnd = project.EstimatedEndDate ? new Date(project.EstimatedEndDate) : null;
        const jobStart = project.JobStartDate ? new Date(project.JobStartDate) : null;
        const isCompleted = project.Status === "COMPLETED";

        const totalTasks = project.TaskSummary?.TotalTasks || 0;
        const completedTasks = project.TaskSummary?.COMPLETED || 0;
        const progress = totalTasks > 0 ? (completedTasks / totalTasks) * 100 : 0;

        if (!isCompleted && estimatedEnd && now > estimatedEnd) {
            return "Overdue";
        }

        if (!isCompleted && jobStart && now > jobStart && progress < 10) {
            return "Delayed";
        }

        if (isCompleted) return "Completed";
        if (project.Status === "IN-PROGRESS") return "In Progress";
        if (project.Status === "NOT-STARTED") return "To Do";

        return "On Track";
    };

    // Sorted chronologically (newest first)
    const criticalProjects = [...projects]
        .map((p) => ({ ...p, calculatedState: calculateProjectState(p) }))
        .sort((a, b) => {
            const dateA = new Date(a.CreatedAt || a.StartDate).getTime();
            const dateB = new Date(b.CreatedAt || b.StartDate).getTime();
            return dateB - dateA;
        })
        .slice(0, 5);

    return (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
            <Box display="flex" justifyContent="space-between" alignItems="center">
                <Box>
                    <Typography level="title-lg">Product roadmap</Typography>
                    <Typography level="body-md">Real-time critical project tracking</Typography>
                </Box>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                    <Button
                        size="sm"
                        variant="soft"
                        startDecorator={<Plus size={16} />}
                        onClick={() => navigate("/project/new-v2")}
                    >
                        Add new
                    </Button>
                    <IconButton>
                        <MoreHorizontal
                            size={20}
                            color="var(--standard-palette-neutral-500)"
                            style={{ cursor: "pointer" }}
                        />
                    </IconButton>
                </Box>
            </Box>

            <Box display={"flex"} gap={2}>
                <Divider
                    orientation={"vertical"}
                    sx={{ p: 0.3, backgroundColor: "var(--standard-palette-danger-400)", borderRadius: "sm" }}
                />
                <Table
                    sx={{
                        "--TableCell-paddingX": "16px",
                        "--TableCell-paddingY": "16px",
                        "& tr > *:last-child": { textAlign: "right" },
                        "& thead th": { borderBottom: "1px solid", borderColor: "divider" },
                        "& tbody tr": { borderBottom: "1px solid", borderColor: "divider" },
                        "& tr": { bgcolor: "transparent !important" },
                    }}
                >
                    <thead>
                        <tr>
                            <th>
                                <Typography level="title-sm">Name</Typography>
                            </th>
                            <th>
                                <Typography level="title-sm">ETA</Typography>
                            </th>
                            <th>
                                <Typography level="title-sm">Salesman</Typography>
                            </th>
                            <th>
                                <Typography level="title-sm">Members</Typography>
                            </th>
                            <th style={{ width: 160 }}>
                                <Typography level="title-sm">Progress</Typography>
                            </th>
                            <th>
                                <Typography level="title-sm">State</Typography>
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        {criticalProjects.map((project: any, i: number) => {
                            const config = STATUS_UI_CONFIG[project.calculatedState] || STATUS_UI_CONFIG["On Track"];
                            const displayDate = new Date(
                                project.EstimatedEndDate || project.StartDate,
                            ).toLocaleDateString("en-GB", {
                                day: "2-digit",
                                month: "short",
                                year: "numeric",
                            });

                            const totalTasks = project.TaskSummary?.TotalTasks || 0;
                            const completedTasks = project.TaskSummary?.COMPLETED || 0;
                            const progressPercentage =
                                totalTasks > 0 ? Math.round((completedTasks / totalTasks) * 100) : 0;

                            return (
                                <tr
                                    key={project.Id || i}
                                    style={{ cursor: "pointer" }}
                                    onClick={() => navigate(`/project/list/${project.Id}`)}
                                >
                                    <td>
                                        <Typography
                                            level="body-sm"
                                            noWrap
                                            sx={{
                                                maxWidth: 240,
                                                overflow: "hidden",
                                                textOverflow: "ellipsis",
                                                display: "block",
                                                fontWeight: "600",
                                                color: "text.primary",
                                                "&:hover": {
                                                    textDecoration: "underline",
                                                },
                                            }}
                                        >
                                            {project.Name}
                                        </Typography>
                                    </td>
                                    <td>
                                        <Stack direction="row" spacing={1.5} alignItems="center">
                                            <Calendar size={18} color="var(--standard-palette-neutral-400)" />
                                            <Typography level="body-sm">{displayDate}</Typography>
                                        </Stack>
                                    </td>
                                    <td>
                                        <Stack direction="row" spacing={1} alignItems="center">
                                            <Avatar
                                                size="sm"
                                                sx={{ "--Avatar-size": "32px" }}
                                                src={getAvatarUrl((i % 12) + 1)}
                                            />
                                            <Typography level="body-sm">{project.SalesmanName || "N/A"}</Typography>
                                        </Stack>
                                    </td>
                                    <td>
                                        <AvatarGroup size="sm" sx={{ "--Avatar-size": "32px" }}>
                                            <Avatar src={getAvatarUrl(((i + 1) % 12) + 1)} />
                                            <Avatar src={getAvatarUrl(((i + 2) % 12) + 1)} />
                                            <Avatar src={getAvatarUrl(((i + 3) % 12) + 1)} />
                                        </AvatarGroup>
                                    </td>
                                    <td>
                                        <Box sx={{ width: 140 }}>
                                            <LinearProgress
                                                determinate
                                                value={progressPercentage}
                                                color={config.color as any}
                                                thickness={8}
                                                sx={{
                                                    borderRadius: 0,
                                                    bgcolor: `${config.color}.100`,
                                                }}
                                            />
                                        </Box>
                                    </td>
                                    <td>
                                        <Stack
                                            direction="row"
                                            spacing={1.5}
                                            alignItems="center"
                                            justifyContent="flex-end"
                                        >
                                            <Box
                                                sx={{
                                                    color: `${config.color}.500`,
                                                    display: "flex",
                                                    alignItems: "center",
                                                }}
                                            >
                                                {config.icon}
                                            </Box>
                                            <Typography level="body-sm" color={config.color as any}>
                                                {config.label}
                                            </Typography>
                                        </Stack>
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </Table>
            </Box>
        </Box>
    );
}
